import { z } from 'zod';
import type { Character } from '@base/types';
import { MODEL_PROVIDER_NAMES } from './modelProviders';
import { CLIENT_NAMES } from './clients';
import { PLUGIN_NAMES } from './plugins';

export const settingsSchema = z.object({
  secrets: z.record(z.string().min(1, 'value is missing')).and(
    z.object({
      TWITTER_USERNAME: z
        .string()
        .min(1, 'value is missing')
        .refine((val) => !val.includes('@'), {
          message: "TWITTER_USERNAME: username handle must not contain '@'",
        })
        .optional(),
      TWITTER_2FA_SECRET: z
        .string()
        .min(16, {
          message:
            'TWITTER_2FA_SECRET: 2FA secret should be 16 characters long. Available on X / Twitter: Security and account access.',
        })
        .optional(),
    }),
  ),
  voice: z.object({
    model: z.string().optional(),
  }),
  additionalSecrets: z
    .array(z.record(z.string().min(1, 'value is missing')))
    .optional(),
});

export type SettingsSchema = z.infer<typeof settingsSchema>;

/** Schema for the form builder, which is
 * slightly different than the final characterfile.
 * We need it due to how React Hook Form handles arrays.
 */
export const characterFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  avatar: z.string().optional(),
  frameworkVersion: z.string().optional(),
  username: z.string().optional(),
  plugins: z.array(z.enum(PLUGIN_NAMES)),
  modelProvider: z.enum(MODEL_PROVIDER_NAMES, {
    errorMap: (_, __) => {
      return { message: 'Select a model provider' };
    },
  }),
  clients: z
    .array(z.enum(CLIENT_NAMES))
    .min(1, 'At least one client is required'),
  settings: settingsSchema,
  bio: z.array(
    z.object({
      name: z.string().min(1, 'Bio is required'),
    }),
  ),
  lore: z.array(
    z.object({
      name: z.string().min(1, 'Lore is required'),
    }),
  ),
  knowledge: z.array(z.object({ name: z.string() })).optional(),
  messageExamples: z
    .array(
      z
        .array(
          z.object({
            user: z.string().min(1, 'User is required'),
            content: z.object({
              text: z.string().min(1, 'Message example is required'),
            }),
          }),
        )
        .min(2),
    )
    .min(1, 'At least one message example is required'),
  postExamples: z.array(
    z.object({
      name: z.string().min(1, 'Post example is required'),
    }),
  ),
  style: z.object({
    all: z.array(
      z.object({
        name: z.string().min(1, `Style for 'All' is required`),
      }),
    ),
    chat: z.array(
      z.object({
        name: z.string().min(1, `Style for 'Chat' is required`),
      }),
    ),
    post: z.array(
      z.object({
        name: z.string().min(1, `Style for 'Post' is required`),
      }),
    ),
  }),
  topics: z.array(z.string().min(1)).min(1, 'At least one topic is required'),
  adjectives: z
    .array(z.string().min(1))
    .min(1, 'At least one adjective is required'),
});

export type CharacterFormSchema = z.infer<typeof characterFormSchema>;

export const characterDraftFormSchema = z.object({
  name: z.string(),
  avatar: z.string().optional(),
  frameworkVersion: z.string().optional(),
  username: z.string().optional(),
  plugins: z.array(z.enum(PLUGIN_NAMES)),

  modelProvider: z.union([
    z.enum(MODEL_PROVIDER_NAMES, {
      errorMap: (_, __) => {
        return { message: 'Select a model provider' };
      },
    }),
    z.literal(''),
  ]),
  clients: z.array(z.enum(CLIENT_NAMES)),
  settings: settingsSchema,
  bio: z.array(
    z.object({
      name: z.string(),
    }),
  ),
  lore: z.array(
    z.object({
      name: z.string(),
    }),
  ),
  knowledge: z.array(z.object({ name: z.string() })).optional(),
  messageExamples: z.array(
    z.array(
      z.object({
        user: z.string(),
        content: z.object({
          text: z.string(),
        }),
      }),
    ),
  ),
  postExamples: z.array(
    z.object({
      name: z.string(),
    }),
  ),
  style: z.object({
    all: z.array(
      z.object({
        name: z.string(),
      }),
    ),
    chat: z.array(
      z.object({
        name: z.string(),
      }),
    ),
    post: z.array(
      z.object({
        name: z.string(),
      }),
    ),
  }),
  topics: z.array(z.string().min(1)),
  adjectives: z.array(z.string().min(1)),
});

export type CharacterDraftFormSchema = z.infer<typeof characterDraftFormSchema>;

/** Schema for the characterfile JSON file */
export const characterfileSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  username: z.string().optional(),
  plugins: z.array(
    z.enum(PLUGIN_NAMES, {
      errorMap: (_, __) => {
        return { message: 'Unsupported or invalid plugin' };
      },
    }),
  ),
  modelProvider: z.enum(MODEL_PROVIDER_NAMES, {
    errorMap: (_, __) => {
      return { message: 'Unsupported or invalid model provider' };
    },
  }),
  clients: z
    .array(
      z.enum(CLIENT_NAMES, {
        errorMap: (_, __) => {
          return { message: 'Unsupported or invalid client' };
        },
      }),
    )
    .min(1, 'At least one client is required'),
  settings: settingsSchema,
  bio: z.array(z.string().min(1, 'Bio is required')),
  lore: z.array(z.string().min(1, 'Lore is required')),
  knowledge: z.array(z.string()).optional(),
  messageExamples: z
    .array(
      z
        .array(
          z.object({
            user: z.string().min(1, 'User is required'),
            content: z.object({
              text: z.string(),
            }),
          }),
        )
        .min(2),
    )
    .min(1, 'At least one message example is required'),
  postExamples: z.array(z.string().min(1, 'Post example is required')),
  style: z.object({
    all: z.array(z.string().min(1, `Style for 'All' is required`)),
    chat: z.array(z.string().min(1, `Style for 'Chat' is required`)),
    post: z.array(z.string().min(1, `Style for 'Post' is required`)),
  }),
  topics: z.array(z.string().min(1)).min(1, 'At least one topic is required'),
  adjectives: z
    .array(z.string().min(1))
    .min(1, 'At least one adjective is required'),
}) satisfies z.ZodType<Character>;

export type CharacterfileSchema = z.infer<typeof characterfileSchema>;
